<template>
  <AdminLayout v-if="userCanSendAlerts" section="Alert History" title="Alert History" hideHeader>
    <div class="pb-4">
      <BackButton to=".."> Back to Alert </BackButton>
    </div>
    <h1 class="py-3 mb-6 border-b flex">
      <div class="text-xl">
        <div class="font-bold">Alert History</div>
        <div>
          {{ property?.name }}
        </div>
      </div>
    </h1>

    <div class="flex flex-wrap gap-4 mb-4 items-end">
      <div>
        <label class="block text-sm font-medium mb-1">Start Date</label>
        <input type="date" v-model="startDate" class="border rounded px-2 py-1 text-sm" />
      </div>
      <div>
        <label class="block text-sm font-medium mb-1">End Date</label>
        <input type="date" v-model="endDate" class="border rounded px-2 py-1 text-sm" />
      </div>
      <div class="flex gap-2 items-center text-xs">
        <button class="text-blue-600 hover:underline" @click="setRange(30)">30 days</button>
        <button class="text-blue-600 hover:underline" @click="setRange(90)">90 days</button>
        <button class="text-blue-600 hover:underline" @click="setRange(182)">6 months</button>
        <button class="text-blue-600 hover:underline" @click="setRange(365)">12 months</button>
      </div>
    </div>

    <div class="mt-4">
      <Loading v-if="isPropertyLoading || isLoading" />
      <div v-else>
        <div class="mt-4" v-if="sortedHistories.length > 0">
          <!-- Summary metric cards: wrap with browser width -->
          <div class="flex flex-wrap gap-3 mb-4">
            <div
              v-for="metric in summaryMetrics"
              :key="metric.label"
              class="flex flex-col px-4 py-2 border rounded-lg bg-white shadow-sm min-w-[9rem]"
            >
              <span class="text-xs text-gray-500">{{ metric.label }}</span>
              <span class="text-2xl font-bold">{{ metric.value }}</span>
              <span v-if="metric.note" class="text-xs text-gray-400">{{ metric.note }}</span>
            </div>
          </div>
          <div v-if="sortedHistories.length === MAX_RESULTS" class="mb-4 text-sm">
            Showing the {{ MAX_RESULTS }} most recent alerts in this date range.
          </div>
          <table class="table-auto w-full border-2 shadow text-sm">
            <thead class="font-sm font-bold">
              <tr>
                <td class="pl-3"></td>
                <td class="pl-3 py-2 cursor-pointer" @click="changeSort(SortColumn.SentBy)">
                  Sent By <UpDownArrow v-if="columnToSort === SortColumn.SentBy" :is-down="isSortingDown" />
                </td>
                <td class="px-3 cursor-pointer" @click="changeSort(SortColumn.DateSent)">
                  Date Sent <UpDownArrow v-if="columnToSort === SortColumn.DateSent" :is-down="isSortingDown" />
                </td>
                <td class="px-3 cursor-pointer text-center" @click="changeSort(SortColumn.Recipients)">
                  Recipients <UpDownArrow v-if="columnToSort === SortColumn.Recipients" :is-down="isSortingDown" />
                </td>
                <td
                  class="px-3 cursor-pointer whitespace-nowrap"
                  title="Message characters"
                  @click="changeSort(SortColumn.Length)"
                >
                  Length <UpDownArrow v-if="columnToSort === SortColumn.Length" :is-down="isSortingDown" />
                </td>
                <td class="px-3 cursor-pointer" @click="changeSort(SortColumn.Message)">
                  Message <UpDownArrow v-if="columnToSort === SortColumn.Message" :is-down="isSortingDown" />
                </td>
              </tr>
            </thead>
            <template v-for="(history, i) in sortedHistories" :key="i">
              <tr
                class="font-xs font-mono border-bottom-2 hover:border-sky-800 hover:bg-sky-300 cursor-pointer"
                :class="{ 'bg-stone-200': i % 2 === 0 }"
                @click="selectClick(history)"
              >
                <td class="pl-3">
                  <div class="px-2">
                    <IHeroiconsOutlineChevronUp
                      class="mx-auto"
                      :class="{
                        'transform rotate-180': history.alertHistoryId !== selectedHistoryId,
                      }"
                    />
                  </div>
                </td>
                <td class="pl-3">
                  <span :title="history.senderPersonLoginEmail || undefined">
                    {{ senderName(history) }}
                  </span>
                </td>
                <td class="px-3 whitespace-nowrap">{{ formatDateTime(history.dateSent) }}</td>
                <td class="px-3 text-center">
                  <span :title="`Email: ${history.emailCount ?? 0}  SMS: ${history.smsCount ?? 0}  Push: ${history.pushCount ?? 0}  People: ${history.peopleCount ?? 0}`">
                    {{ history.recipientCount }}
                  </span>
                </td>
                <td class="px-3 whitespace-nowrap">
                  {{ smsCharCount(smsText(history)) }}
                </td>
                <td class="px-3 max-w-xs">
                  <span
                    :title="history.messageText || undefined"
                    class="block whitespace-nowrap overflow-hidden text-ellipsis max-w-xs"
                  >{{ history.messageText }}</span>
                </td>
              </tr>
              <template v-if="history.alertHistoryId === selectedHistoryId">
                <tr>
                  <td :colspan="6" class="px-8 p-6 break-all">
                    <div class="font-bold">Message</div>
                    {{ history.messageText }}
                    <div class="mt-2 font-bold">Mobile Message (SMS / Push)</div>
                    {{ mobileMessageDisplay(history.messageText, history.mobileMessage) }}
                    <div class="mt-2 font-bold">Email List</div>
                    {{ history.emailList?.length ? history.emailList : "None." }}
                    <div class="mt-2 font-bold">SMS List</div>
                    {{ history.smsList?.length ? history.smsList : "None." }}
                    <div class="mt-2 font-bold">Push Notifications</div>
                    {{ history.pushList?.length ? history.pushList : "None." }}
                  </td>
                </tr>
              </template>
            </template>
          </table>
        </div>
        <div v-else class="mb-2">
          <span>-- No Alert History found for this building in this date range --</span>
        </div>
      </div>
    </div>
  </AdminLayout>
  <AdminLayout v-else page="Alert History" title="Alert History">
    You need permission to view this page.
  </AdminLayout>
</template>

<script lang="ts" setup>
import { useAlertStore } from "../../store/alertStore";
import { useBldgStore } from "../../store/bldgStore";
import { useUserStore } from "../../store/userStore";
import { AlertHistoryDTO, PermissionType } from "../../store/generatedFetchClient";
import { formatDateTime, mobileMessageDisplay, smsMessageText } from "../../helpers";
import { smsCharCount, smsRecipientCount } from "../../helpers/gsm";

const bldgStore = useBldgStore();
const alertStore = useAlertStore();

// Does user have permission to send alerts
var userCanSendAlerts = computed(() =>
  useUserStore().hasPermission(PermissionType.SendAlerts)
);

var isPropertyLoading = computed(() => bldgStore.isLoading);
var isLoading = ref(false);
var property = computed(() => bldgStore.property);

var histories = computed(() => alertStore.histories);
const MAX_RESULTS = 1000;

//#region Date range
function getDateString(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().split("T")[0];
}

const startDate = ref<string>(getDateString(90));
const endDate = ref<string>(getDateString(0));

function setRange(days: number) {
  startDate.value = getDateString(days);
  endDate.value = getDateString(0);
}

const dateRangeDays = computed(() => {
  if (!startDate.value || !endDate.value) return 0;
  const ms = new Date(endDate.value).getTime() - new Date(startDate.value).getTime();
  return Math.round(ms / 86_400_000);
});

watch([startDate, endDate], ([newStart, newEnd]) => {
  if (newStart && newEnd) loadData();
});
//#endregion

onMounted(loadData);
watch(property, loadData);

async function loadData() {
  if (userCanSendAlerts.value && property.value?.portalId) {
    isLoading.value = true;
    await alertStore.getHistory(
      property.value.portalId,
      MAX_RESULTS,
      startDate.value || undefined,
      endDate.value || undefined
    );
    isLoading.value = false;
  }
}

//#region Columns, sorting and metrics
// Length counts the text actually sent by SMS (mobile message when set)
function smsText(history: AlertHistoryDTO) {
  return smsMessageText(history.messageText, history.mobileMessage);
}

function senderName(history: AlertHistoryDTO) {
  return (
    [history.senderPersonFirstName, history.senderPersonLastName].filter(Boolean).join(" ") ||
    history.senderPersonLoginEmail
  );
}

// EmailList is stored comma-separated, like SmsList
function emailRecipientCount(history: AlertHistoryDTO) {
  return (history.emailList ?? "").split(",").filter((e) => e.trim().length > 0).length;
}

enum SortColumn {
  SentBy,
  DateSent,
  Recipients,
  Length,
  Message,
}

const columnToSort = ref(SortColumn.DateSent);
const isSortingDown = ref(true);

function changeSort(column: SortColumn) {
  if (column === columnToSort.value) {
    isSortingDown.value = !isSortingDown.value;
  } else {
    columnToSort.value = column;
    isSortingDown.value = false;
  }
}

const sortedHistories = computed(() => {
  const rows = histories.value?.slice() ?? [];
  rows.sort((a, b) => {
    switch (columnToSort.value) {
      case SortColumn.SentBy:
        return (
          (a.senderPersonFirstName ?? "").localeCompare(b.senderPersonFirstName ?? "") ||
          (a.senderPersonLastName ?? "").localeCompare(b.senderPersonLastName ?? "")
        );
      case SortColumn.DateSent:
        return (
          (a.dateSent ? new Date(a.dateSent).getTime() : 0) -
          (b.dateSent ? new Date(b.dateSent).getTime() : 0)
        );
      case SortColumn.Recipients:
        return (a.recipientCount ?? 0) - (b.recipientCount ?? 0);
      case SortColumn.Length:
        return smsCharCount(smsText(a)) - smsCharCount(smsText(b));
      case SortColumn.Message:
        return (a.messageText ?? "").localeCompare(b.messageText ?? "");
    }
  });
  if (isSortingDown.value) rows.reverse();
  return rows;
});

// Totals for the rows shown (no cost metrics on the client page)
const summaryMetrics = computed(() => {
  const rows = sortedHistories.value;
  const sum = (count: (h: AlertHistoryDTO) => number) =>
    rows.reduce((total, h) => total + count(h), 0).toLocaleString();
  return [
    {
      label: "Alerts sent",
      value: rows.length.toLocaleString(),
      note: `last ${dateRangeDays.value} days`,
    },
    { label: "SMS messages", value: sum((h) => smsRecipientCount(h.smsList)) },
    { label: "Email messages", value: sum(emailRecipientCount) },
    { label: "Push notifications", value: sum((h) => h.pushCount ?? 0) },
  ] as { label: string; value: string; note?: string }[];
});
//#endregion

let selectedHistoryId = ref(-1);

// Same item clicked twice => Toggle selection
function selectClick(history: AlertHistoryDTO) {
  selectedHistoryId.value =
    history.alertHistoryId === selectedHistoryId.value ? -1 : history.alertHistoryId;
}
</script>
